import {
  Box,
  Button,
  Switch,
  useTheme,
  TextField,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
  FormControlLabel,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { Role } from 'library/types';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { GridRowId } from '@mui/x-data-grid-premium';
import { renderAutoCompleteLocaleText } from 'utils';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { useCallback, useEffect, useState } from 'react';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

export type UpdateUserFormParams = {
  nameSurname: string;
  roleId: number | null;
  isActivityUser: boolean;
};

export type UpdateUserPostParams = {
  id: number;
  nameSurname: string;
  roleId: number;
  isActivityUser: boolean;
};

const defaultValues: UpdateUserFormParams = {
  nameSurname: '',
  roleId: null,
  isActivityUser: true,
};

type UpdateUserDialogProps = {
  open: boolean;
  userId: GridRowId;
  handleClose: () => void;
  roles: Role[];
  refresh: () => Promise<void>;
};

const UpdateUserDialog = ({ open, userId, handleClose: closeDialog, roles, refresh }: UpdateUserDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // form validations & form state
  const schema = yup.object().shape({
    nameSurname: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    roleId: yup
      .number()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
  });

  const {
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateUserFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // async helper functions
  const onSubmit = async (data: UpdateUserFormParams) => {
    const postData: UpdateUserPostParams = {
      id: userId as number,
      nameSurname: data.nameSurname,
      roleId: data.roleId as number,
      isActivityUser: data.isActivityUser,
    };

    try {
      setLoading(true);

      const response = await apiService.updateUser(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="users.updateUserCompleted"
            defaultMessage="Kullanıcı düzenleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getSpecificUser = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificUser(userId as number);

      if (response.isSuccess) {
        setValue('nameSurname', response.data.nameSurname);
        setValue('roleId', response.data.roleId);
        setValue('isActivityUser', response.data.isActivityUser ?? false);

        const sRole = roles.find((role) => role.id === response.data.roleId);

        if (sRole) {
          setSelectedRole(sRole);
        }
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [userId, setValue, roles]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    setSelectedRole(null);
    reset();
  };

  // useEffect hooks
  useEffect(() => {
    if (open) {
      getSpecificUser();
    }
  }, [open, getSpecificUser]);

  return (
    <ResponsiveDialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="users.updateUser" defaultMessage="Kullanıcı Düzenle" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <ResponsiveDialogContent>
          <DialogContentText>
            <FormattedMessage
              id="users.updateUserText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili kullanıcı için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="nameSurname"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    label={<FormattedMessage id="users.nameSurname" defaultMessage="Adı" />}
                    fullWidth
                    size="small"
                    error={!!errors.nameSurname}
                    helperText={errors.nameSurname?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Autocomplete
                size="small"
                options={roles}
                value={selectedRole}
                {...renderAutoCompleteLocaleText(intl)}
                onChange={(_, newValue: Role | null) => {
                  setSelectedRole(newValue);

                  setValue('roleId', newValue ? newValue.id : null);
                }}
                getOptionLabel={(option: Role) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        backgroundColor: 'inherit',
                        width: '2.25rem',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                    }}
                    label={<FormattedMessage id="users.role" defaultMessage="Rol" />}
                    error={!!errors.roleId}
                    helperText={errors.roleId?.message}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="isActivityUser"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label={<FormattedMessage id="users.isActivityUser" defaultMessage="Aktivite Kullanıcısı" />}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default UpdateUserDialog;
